import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, MapPin, Calendar, ArrowLeft } from '@phosphor-icons/react';
import { getCategoryLabel } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedSales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSaved = async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`, { withCredentials: true });
      setSales(data.sales || []);
    } catch {
      toast.error('Failed to load saved sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSaved(); }, []);

  const handleUnsave = async (saleId) => {
    try {
      await axios.delete(`${API}/favorites/${saleId}`, { withCredentials: true });
      setSales(prev => prev.filter(s => s.id !== saleId));
      toast.success('Removed from saved');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="saved-sales-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/explore" className="inline-flex items-center gap-2 text-[#5C7A99] hover:text-[#004E89] font-bold text-sm mb-6 transition-colors" data-testid="saved-back-btn">
          <ArrowLeft size={18} weight="bold" /> Back to Explore
        </Link>

        <h1 className="text-3xl sm:text-4xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>
          Saved Sales
        </h1>
        <p className="text-[#5C7A99] mb-8">Your favorited estate and garage sales</p>

        {loading ? (
          <div className="text-center py-20 text-[#5C7A99] font-medium">Loading saved sales...</div>
        ) : sales.length === 0 ? (
          <div className="text-center py-20" data-testid="no-saved-sales">
            <Heart size={48} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
            <p className="text-lg font-bold text-[#004E89] mb-2">No saved sales yet</p>
            <p className="text-[#5C7A99] mb-6">Tap the heart icon on any sale to save it here</p>
            <Link to="/explore">
              <button className="btn-primary py-3 px-6">Explore Sales</button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sales.map(sale => (
              <div key={sale.id} className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89] hover:-translate-y-0.5 transition-transform" data-testid={`saved-sale-${sale.id}`}>
                <Link to={`/sale/${sale.id}`}>
                  {sale.photos?.length > 0 ? (
                    <div className="w-full h-36">
                      <img src={`${API}/files/${sale.photos[0]}`} alt={sale.title} className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }} />
                    </div>
                  ) : (
                    <div className="w-full h-36 bg-gradient-to-br from-[#FFD166] to-[#FF6B35] flex items-center justify-center">
                      <MapPin size={36} weight="duotone" color="#004E89" />
                    </div>
                  )}
                </Link>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`tag text-[10px] tag-${sale.category === 'business_closing' ? 'business' : sale.category}`}>
                      {getCategoryLabel(sale.category)}
                    </span>
                    <button onClick={() => handleUnsave(sale.id)} className="text-red-500 hover:text-red-700 transition-colors" data-testid={`unsave-btn-${sale.id}`}>
                      <Heart size={20} weight="fill" />
                    </button>
                  </div>
                  <Link to={`/sale/${sale.id}`}>
                    <h3 className="font-bold text-[#004E89] text-sm truncate mb-1" style={{ fontFamily: 'Outfit' }}>{sale.title}</h3>
                  </Link>
                  <p className="text-xs text-[#5C7A99] flex items-center gap-1">
                    <MapPin size={12} weight="bold" /> {sale.city}, {sale.state}
                  </p>
                  <p className="text-xs text-[#5C7A99] flex items-center gap-1">
                    <Calendar size={12} weight="bold" /> {new Date(sale.start_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
